import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaStar } from 'react-icons/fa';
import { getProductReviewAndRatingAPI, updateProductRatingReviewAPI } from '../../utils/api';
import Sidebar from '../Sidebar/Sidebar';
import './ProductRatingReview.css';
import Toast from '../../components/Toast/Toast';

const ProductRatingReview = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [toast, setToast] = useState({ show: false, message: '', type: '' });
    const PAGE_LIMIT = 10;

    const showToast = (message, type = "success") => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
    };

    const fetchReviews = async () => {
        setLoading(true);
        const response = await getProductReviewAndRatingAPI({
            product_id: parseInt(id),
            limit: PAGE_LIMIT,
            page: page,
            search: searchTerm
        });
        setLoading(false);
        if (response?.status === 200) {
            setReviews(response?.data);
            setTotalPages(response?.pagination?.total_pages);
        }
    };

    useEffect(() => {
        if (id) {
            fetchReviews();
        }
    }, [page, searchTerm, id]);

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleStatusToggle = async (reviewId, currentStatus) => {
        try {
            const response = await updateProductRatingReviewAPI({
                rating_review_id: reviewId,
                disable: currentStatus == 1 ? 0 : 1
            });

            if (response?.status === 200) {
                fetchReviews();
                showToast('Status updated successfully', 'success');
            } else {
                showToast(typeof response?.message === 'string' ? response?.message : 'Failed to update status', 'error');
            }
        } catch (error) {
            showToast('Failed to update status', 'error');
        }
    };
    const renderStars = (rating) => {
        return Array(5).fill(0).map((_, i) => (
            <FaStar
                key={i}
                className={i < rating ? 'star-filled' : 'star-empty'}
            />
        ));
    };

    return (
        <div className="ProductRatingReview-layout">
            <Toast toast={toast} />
            <Sidebar />
            <main className="ProductRatingReview-main-content">
                <div className="ProductRatingReview-header-row">
                    <div className="header-left">
                        <h1 className="ProductRatingReview-title">Product Reviews & Ratings</h1>
                    </div>
                </div>

                <div className="table-responsive">
                    {loading ? (
                        <div className="loading">Loading...</div>
                    ) : (
                        <table className="ProductRatingReview-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>User</th>
                                    <th>Rating</th>
                                    <th>Review</th>
                                    <th>Color</th>
                                    <th>Size</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reviews.length > 0 ? (
                                    reviews.map((review, index) => (
                                        <tr key={review._id}>
                                            <td>{(page - 1) * PAGE_LIMIT + index + 1}</td>
                                            <td>
                                                <div className="user-info">
                                                    <span className="rating-user-name">{review?.user?.name || 'N/A'}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="rating-stars">
                                                    {renderStars(review?.rating)}
                                                </div>
                                            </td>
                                            <td className="review-text">{review?.review}</td>
                                            <td className="color-name">
                                                {review?.color || 'N/A'}
                                            </td>
                                            <td className="size">
                                                {review?.size?.size || 'N/A'}
                                            </td>
                                            <td>
                                                <div className="action-buttons">
                                                    <button
                                                        className={`status-toggle ${review?.disable === 1 ? 'inactive' : 'active'}`}
                                                        onClick={() => handleStatusToggle(review?._id, review?.disable)}
                                                        title={review?.disable === 1 ? 'Activate Review' : 'Deactivate Review'}
                                                    >
                                                        <span className="toggle-slider"></span>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="no-data">
                                            No reviews found for this product.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}

                    {totalPages > 1 && (
                        <div className="pagination">
                            <button
                                onClick={() => handlePageChange(page - 1)}
                                disabled={page === 1}
                                className="pagination-button"
                            >
                                Previous
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                                <button
                                    key={pageNum}
                                    onClick={() => handlePageChange(pageNum)}
                                    className={`pagination-button ${page === pageNum ? 'active' : ''}`}
                                >
                                    {pageNum}
                                </button>
                            ))}
                            <button
                                onClick={() => handlePageChange(page + 1)}
                                disabled={page === totalPages}
                                className="pagination-button"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default ProductRatingReview;